import React from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Linkedin, 
  Twitter, 
  Github,
  ArrowUp,
  ExternalLink 
} from 'lucide-react';
import logo from "../assets/branding/logo-hero.png";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              {/* <h3 className="text-3xl font-bold text-silver mb-4">HARA-XY</h3> */}
                            <div className="flex-shrink-0 cursor-pointer">
                <img
                  src={logo}
                  alt="HARA-XY Logo"
                  className="h-10 w-auto"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                />
              </div>
            
              <p className="text-gray-400 text-lg leading-relaxed max-w-md">
                Transforming enterprises through innovative IT solutions and strategic consulting. 
                Your trusted partner in digital excellence.
              </p>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3 text-gray-400 hover:text-silver transition-colors duration-300">
                <Mail size={18} className="text-silver" />
                <a href="mailto:hello@hara-xy.com">hello@hara-xy.com</a>
              </div>
              <div className="flex items-center gap-3 text-gray-400 hover:text-silver transition-colors duration-300">
                <Phone size={18} className="text-silver" />
                <a href="tel:+917676984958">+91 7676 984958</a>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <MapPin size={18} className="text-silver" />
                <span>Bengaluru, Bagalkot, Hyderabad, India, Poland & Remote</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 bg-gray-800 hover:bg-silver hover:text-black rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-gray-800 hover:bg-silver hover:text-black rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Twitter size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-gray-800 hover:bg-silver hover:text-black rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Github size={18} />
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-silver font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                'Cloud Solutions',
                'Custom Software Development',
                'Infrastructure Management',
                'Digital Transformation',
                'IT Strategy Consulting',
                'Business Process Optimization'
              ].map((service, index) => (
                <li key={index}>
                  <a 
                    href="#services" 
                    className="text-gray-400 hover:text-silver transition-colors duration-300 text-sm"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-silver font-semibold text-lg mb-6">Company</h4>
            <ul className="space-y-3">
              {[
                { name: 'About Us', href: '#about' },
                { name: 'Case Studies', href: '#case-studies' },
                { name: 'Careers', href: '/hiring' },
                { name: 'Blog', href: '#' },
                { name: 'Privacy Policy', href: '#' },
                { name: 'Terms of Service', href: '#' }
              ].map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    className="text-gray-400 hover:text-silver transition-colors duration-300 text-sm flex items-center gap-1"
                  >
                    {link.name}
                    {link.href === '#' && <ExternalLink size={12} />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="py-8 border-t border-gray-800">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-silver font-semibold text-lg mb-2">Stay Updated</h4>
              <p className="text-gray-400 text-sm">
                Get the latest insights on technology trends and best practices.
              </p>
            </div>
            <div className="flex gap-3 w-full lg:w-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 lg:w-64 px-4 py-3 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver text-sm"
              />
              <button className="bg-silver text-black px-6 py-3 rounded-lg font-semibold text-sm hover:bg-white transition-all duration-300 whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-gray-400 text-sm text-center md:text-left">
            © {currentYear} HARA-XY. All rights reserved. Crafted with precision for enterprise excellence.
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-gray-500 text-xs">
              Trusted by 150+ enterprise clients
            </div>
            <button
              onClick={scrollToTop}
              className="w-10 h-10 bg-silver/20 hover:bg-silver/30 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            >
              <ArrowUp size={18} className="text-silver" />
            </button>
          </div>
        </div>

        {/* Certifications & Compliance */}
        <div className="pb-8">
          <div className="flex flex-wrap justify-center gap-8 text-gray-500 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>ISO 27001 Certified</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span>SOC 2 Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span>GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
              <span>AWS Partner</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;